import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Button, Card, Collapse, Link, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Link as RouterLink } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import EmptyState from '../../merchants/shared/EmptyState'
import { ReadinessChip } from '../../merchants/shared/StateChip'
import { C, RADIUS, SX, TYPE } from '../../merchants/shared/tokens'
import { saveBlob, filenameFromResponse } from '../../freight-profitability/freightPnlUtils'
import { downloadPricingTemplate, errorText, fetchPricingCoverage } from '../ptlSetupApi'
import { fmtDate, num, ROUTES } from '../constants'
import { Banner } from '../components/SetupUi'
import RateCardUpload from './RateCardUpload'

/** Coverage of one merchant's rate cards (GET pricing/coverage). Pass null to skip (another step is open). */
export function usePricing(merchantId) {
  const [coverage, setCoverage] = useState(null)
  const [error, setError] = useState('')
  const reload = useCallback(() => {
    if (!merchantId) return
    setError('')
    fetchPricingCoverage(merchantId).then(setCoverage).catch((e) => setError(errorText(e, 'Could not load the rate cards')))
  }, [merchantId])
  useEffect(() => { setCoverage(null); reload() }, [reload])
  return { coverage, error, reload }
}

/** Standard lanes no card prices: PTL orders on them get no price from any courier. */
const unpricedLanes = (cov) => {
  if (!cov.cards.length) return cov.standardLanes
  const sets = cov.cards.map((c) => new Set(c.missingLanes))
  return [...sets[0]].filter((lane) => sets.every((s) => s.has(lane))).length
}

const lastUpload = (cov) => {
  if (cov.latest) return { at: cov.latest.createdAt, by: cov.latest.createdBy, courier: cov.latest.courier }
  const dates = cov.cards.map((c) => c.uploadedOn).filter(Boolean).sort((a, b) => new Date(b) - new Date(a))
  return dates.length ? { at: dates[0] } : null
}

const dateOnly = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '')

/** KPI cards of Step 1 (plan screen 2): rate cards, lanes priced, lanes with no price, last upload. */
export function pricingKpis(cov) {
  if (!cov) return ['Rate cards', 'Lanes priced', 'Lanes with no price', 'Last upload'].map((label) => ({ key: label, label, value: '' }))
  const n = cov.standardLanes
  const cards = cov.cards
  const lowest = cards.reduce((low, c) => (!low || c.lanesPriced < low.lanesPriced ? c : low), null)
  const unpriced = unpricedLanes(cov)
  const last = lastUpload(cov)
  return [
    { key: 'cards', label: 'Rate cards', value: cards.length ? num(cards.length) : 'None', tone: cards.length ? undefined : 'danger',
      sub: cards.length ? `${cards.length === 1 ? 'Courier' : 'Couriers'}: ${cards.map((c) => c.courier).join(', ')}` : 'Upload one file per courier' },
    { key: 'lanes', label: 'Lanes priced', value: lowest ? `${num(lowest.lanesPriced)} / ${num(n)}` : `0 / ${num(n)}`,
      tone: lowest && lowest.lanesPriced === n ? 'ok' : 'warn',
      sub: !lowest ? 'No card yet' : cards.length > 1 ? `Lowest card: ${lowest.courier}` : `On ${lowest.courier}` },
    { key: 'unpriced', label: 'Lanes with no price', value: num(unpriced), tone: unpriced ? 'danger' : 'ok',
      sub: unpriced ? 'No courier prices them: orders there get no price' : 'Every lane has a price on some courier' },
    { key: 'last', label: 'Last upload', value: last ? dateOnly(last.at) : 'Never',
      sub: last ? [last.courier, last.by].filter(Boolean).join(' · ') || 'Before PTL Merchant Setup' : 'No rate card uploaded' },
  ]
}

/**
 * Step 1 Pricing body (plan screen 2): the merchant's rate cards, one per courier, each against the standard lanes,
 * and the upload of a new or replacement card (its own view: check → preview → confirm).
 */
export default function PricingStep({ merchant, coverage, error, reload, onChanged, onUploadView }) {
  const { enqueueSnackbar } = useSnackbar()
  const [upload, setUpload] = useState(null) // null = list; { courier } = upload view
  // The step footer (Next) hides while the upload view is open: one job on screen at a time.
  useEffect(() => { if (onUploadView) onUploadView(!!upload) }, [upload, onUploadView])
  const [open, setOpen] = useState({})

  const getTemplate = async () => {
    try {
      const res = await downloadPricingTemplate()
      saveBlob(res.data, filenameFromResponse(res, 'ptl_rate_card_template.csv'))
    } catch (e) {
      enqueueSnackbar(errorText(e, 'Could not download the template'), { variant: 'error' })
    }
  }

  const cards = useMemo(() => coverage?.cards || [], [coverage])
  const n = coverage?.standardLanes || 0

  if (error) return <Banner tone='danger' action={<Button size='small' onClick={reload} sx={{ textTransform: 'none' }}>Retry</Button>}>{error}</Banner>
  if (!coverage) return <Typography sx={{ ...TYPE.rowHelp, py: 4, textAlign: 'center' }} role='status'>Loading rate cards…</Typography>

  if (upload) {
    return (
      <RateCardUpload merchant={merchant} presetCourier={upload.courier} existingCouriers={cards.map((c) => c.courier)}
        standardLanes={n} onTemplate={getTemplate} onCancel={() => setUpload(null)}
        onSaved={() => { setUpload(null); reload(); onChanged() }} />
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 1.5, mb: 2 }}>
        <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
          <Typography component='h2' sx={TYPE.sectionTitle}>Rate cards</Typography>
          <Typography sx={{ ...TYPE.rowHelp, mt: 0.25 }}>
            One card per courier, checked against the {num(n)} lanes of the Prozo standard TAT. A lane missing from a card gets no price on that courier.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button variant='outlined' startIcon={<DownloadOutlinedIcon />} onClick={getTemplate} disabled={!n} sx={{ ...SX.ctaGhost, minHeight: 36 }}>
            Download template
          </Button>
          <Button variant='contained' startIcon={<AddIcon />} onClick={() => setUpload({ courier: null })} disabled={!n} sx={{ ...SX.cta, minHeight: 36, whiteSpace: 'nowrap' }}>
            Upload rate card
          </Button>
        </Box>
      </Box>

      {!n && (
        <Banner tone='warn' action={<Button size='small' component={RouterLink} to={ROUTES.tat} sx={{ textTransform: 'none' }}>Open Merchant TAT</Button>}>
          The standard TAT is not uploaded yet, so rate card lanes cannot be checked. Upload the standard TAT first.
        </Banner>
      )}

      {cards.length === 0 ? (
        <EmptyState dense title='No PTL rate card yet'
          body='Download the template, fill the prices for each lane (one row per lane and weight slab), and upload one file per courier.'
          action={n ? <Button variant='contained' startIcon={<AddIcon />} onClick={() => setUpload({ courier: null })} sx={SX.cta}>Upload rate card</Button> : null} />
      ) : (
        <Card elevation={0} sx={{ ...SX.cardFlush, overflowX: 'auto' }}>
          <Table size='small' sx={{ minWidth: 760 }} aria-label='Rate cards of this merchant'>
            <TableHead>
              <TableRow>
                {['Courier', 'Lanes priced', 'Missing lanes', 'Old lane codes', 'Live since', ''].map((h) => (
                  <TableCell key={h} sx={{ ...TYPE.eyebrow, fontSize: 10, color: C.inkFaint, bgcolor: C.surfaceAlt, borderColor: C.line, py: 1.25, whiteSpace: 'nowrap' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {cards.map((c) => {
                const full = n > 0 && c.lanesPriced === n
                const isOpen = !!open[c.courier]
                const share = n ? c.lanesPriced / n : 0
                return (
                  <React.Fragment key={c.courier}>
                    <TableRow sx={{ '& td': { borderColor: C.lineSoft, py: 1.25 } }}>
                      <TableCell sx={{ ...TYPE.rowLabel, whiteSpace: 'nowrap' }}>{c.courier}</TableCell>
                      <TableCell sx={{ minWidth: 200 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box role='progressbar' aria-label={`${c.courier} lanes priced`} aria-valuemin={0} aria-valuemax={n} aria-valuenow={c.lanesPriced}
                            sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: C.lineSoft, overflow: 'hidden' }}>
                            <Box sx={{ width: `${Math.max(2, share * 100)}%`, height: '100%', bgcolor: full ? C.ok : C.warn }} />
                          </Box>
                          <Typography component='span' sx={{ ...TYPE.value, fontSize: 12.5, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                            {num(c.lanesPriced)} / {num(n)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {c.missingLanes.length ? (
                          <Button size='small' onClick={() => setOpen({ ...open, [c.courier]: !isOpen })} aria-expanded={isOpen}
                            endIcon={<ExpandMoreIcon sx={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />}
                            sx={{ textTransform: 'none', fontWeight: 600, fontSize: 12.5, color: C.warn, minHeight: 32, px: 1 }}>
                            {num(c.missingLanes.length)} lane{c.missingLanes.length > 1 ? 's' : ''}
                          </Button>
                        ) : <ReadinessChip tone='done' label='None' />}
                      </TableCell>
                      <TableCell>
                        {c.unknownLanes.length ? (
                          <Tooltip arrow title={`Lane codes not in the standard TAT, so pricing never reaches them: ${c.unknownLanes.slice(0, 12).join(', ')}${c.unknownLanes.length > 12 ? ' …' : ''}`}>
                            <span><ReadinessChip tone='pending' label={`${num(c.unknownLanes.length)} old`} /></span>
                          </Tooltip>
                        ) : <ReadinessChip tone='none' label='None' />}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12.5, color: C.inkMuted, whiteSpace: 'nowrap' }}>{dateOnly(c.uploadedOn) || '—'}</TableCell>
                      <TableCell align='right'>
                        <Button size='small' variant='outlined' onClick={() => setUpload({ courier: c.courier })} sx={{ ...SX.ctaGhost, fontSize: 12.5, minHeight: 32 }}>
                          Replace
                        </Button>
                      </TableCell>
                    </TableRow>
                    {c.missingLanes.length > 0 && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ p: 0, borderColor: isOpen ? C.lineSoft : 'transparent' }}>
                          <Collapse in={isOpen} unmountOnExit>
                            <Box sx={{ px: 2, py: 1.5, bgcolor: C.surfaceSunken }}>
                              <Typography sx={{ ...TYPE.rowHelp, mb: 1 }}>Lanes with no {c.courier} price. Add them to the file and upload it again.</Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {c.missingLanes.map((lane) => (
                                  <Box key={lane} component='span' sx={{ ...SX.pill, fontFamily: 'JetBrains Mono, monospace', fontWeight: 600, color: C.inkBody, bgcolor: C.surface, border: `1px solid ${C.line}`, borderRadius: RADIUS.sm }}>
                                    {lane}
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                )
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      <Typography sx={{ ...TYPE.rowHelp, mt: 1.5 }}>
        TAT is not taken from rate cards: it is set in Step 2. See the rates themselves in{' '}
        <Link component={RouterLink} to={`/merchant-config/merchant/${merchant.id}?tab=3`}>Merchant details → Rate Card</Link>.
        {coverage.latest && <> Last upload here: {fmtDate(coverage.latest.createdAt)}{coverage.latest.createdBy ? ` by ${coverage.latest.createdBy}` : ''}.</>}
      </Typography>
    </Box>
  )
}
